import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dyn, dynStyle } from "trader.ts/ui";
import { Form, constantField, selectBox, textBox } from "trader.ts/ui.forms";
import { checkAllCasesHandled } from "trader.ts/utils";
import {
  b2fileid,
  enrollment_status,
  filenoteid,
  lessongroupid,
  lessonid,
  studentid,
  teacherid,
  textnoteid,
} from "../../../sql/classy/types";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { renderFileInput } from "../../client/fileinput";
import { renderCancelButton, renderSaveButton } from "../../client/savebutton";
import { arrayBufferToBase64Client } from "../../lib/arraybuffertobase64";
import { ddMMM, renderFirstNameLastName } from "../../render";
import * as routes from "../../routes";
import { stringToJsxClientside } from "../editemailtemplates/client";
import { errorField } from "../planning/lessongroupform";
import { mandatoryValidation } from "../../lib/validate";

export type textnote = {
  id: textnoteid;
  lessonid: lessonid;
  content: string;
  stamp: joda.LocalDateTime;
  candelete: boolean;
  only_visible_for_single_student: null | studentid;
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
  } | null;
  teacher: {
    id: teacherid;
    firstname: string;
    lastname: string;
  } | null;
};

export type filenote = {
  id: filenoteid;
  lessonid: lessonid;
  description: string;
  stamp: joda.LocalDateTime;
  mimetype: string;
  storagefileid: b2fileid;
  candelete: boolean;
  student: {
    id: studentid;
    firstname: string;
    lastname: string;
  } | null;
  teacher: {
    id: teacherid;
    firstname: string;
    lastname: string;
  } | null;
};

type deletemode = "nothing" | "confirming" | "deleted";

type writemode =
  | {
      tag: "writingtextnote";
    }
  | { tag: "writingfilenote" };

export function renderPerLesson(
  schoolname: string,
  lessonid: lessonid,
  notes: {
    textnotes: textnote[];
    filenotes: filenote[];
  },
  lessongroup: {
    id: lessongroupid;
    students: {
      id: studentid;
      firstname: string;
      lastname: string;
      enrollment_status: enrollment_status;
    }[];
  },
  opts: {
    writingS?: Source<null | writemode>;
    smallbuttons: boolean;
    allowInput?: boolean;
    reload?: () => void;
    fullWidthButtons: boolean;
    userIsStudent: boolean;
  },
): HTMLElement | HTMLElement[] {
  const existingNotesRendered = (notes.filenotes as (textnote | filenote)[])
    .filter((n) => n.lessonid === lessonid)
    .concat(notes.textnotes.filter((n) => n.lessonid === lessonid))
    .flatMap((n) =>
      renderExistingNote(
        schoolname,
        n,
        opts,
        opts.userIsStudent
          ? { tag: "no" }
          : { tag: "yes", students: lessongroup.students },
      ),
    );
  const writingS = opts.writingS || new Source(null);

  const inputs =
    opts.allowInput === false ? (
      <span></span>
    ) : (
      renderNewNoteForm(lessonid, writingS, lessongroup, opts)
    );

  return existingNotesRendered.concat(inputs);
}

function renderNewNoteForm(
  lessonid: lessonid,
  writingS: Source<writemode | null>,
  lessongroup: {
    id: lessongroupid;
    students: {
      id: studentid;
      firstname: string;
      lastname: string;
      enrollment_status: enrollment_status;
    }[];
  },
  opts: {
    smallbuttons: boolean;
    reload?: () => void;
    fullWidthButtons: boolean;
    userIsStudent: boolean;
  },
) {
  return dyn(writingS, function (writing) {
    if (writing === null) {
      // Not editing -> show buttons to start editing
      return (
        <div
          style={
            "text-align: right;" +
            (opts.fullWidthButtons
              ? "display: flex; color: var(--grey-700);"
              : "")
          }
        >
          <button
            className={
              opts.smallbuttons ? style.button_small : style.button_em_down
            }
            style={
              opts.fullWidthButtons
                ? "flex: 1; border-radius: 8px 0px 0px 8px; color: inherit;"
                : ""
            }
            onclick={() => writingS.set({ tag: "writingtextnote" })}
          >
            <i className={FA.icon_sticky_note} style="color: inherit;"></i>
            {"Notitie"}
          </button>
          <button
            className={
              opts.smallbuttons ? style.button_small : style.button_em_down
            }
            style={
              opts.fullWidthButtons
                ? "flex: 1; margin-left: 0px; border-left: 1px solid var(--grey-200); border-radius: 0px 8px 8px 0px; color: inherit;"
                : ""
            }
            onclick={() => writingS.set({ tag: "writingfilenote" })}
          >
            <i className={FA.icon_paperclip_solid} style="color: inherit;"></i>
            {"Bestand"}
          </button>
        </div>
      );
    } else if (writing.tag === "writingtextnote") {
      const form = new Form({})
        .addField("content", [], () =>
          textBox({
            validations: [mandatoryValidation],
            label: "Nieuwe notitie",
            type: "textarea",
          }),
        )
        .addField("warning_absence", ["content"], ({ content }) => {
          const forbiddenWords = ["afwezig", "ziekte", "aanwezig"];
          function findForbiddenWord(s: string): null | string {
            const lower = s.toLowerCase();
            for (const fb of forbiddenWords) {
              if (lower.includes(fb)) {
                return fb;
              }
            }
            return null;
          }

          const forbiddenWordFound = findForbiddenWord(content);
          if (forbiddenWordFound !== null) {
            return errorField(
              stringToJsxClientside(
                "<section style='color:red;'>Omdat je het woord <b>%word</b> gebruikte in je bericht wil je waarschijnlijk een afwezigheid registreren. Click daarvoor eerst op <b>Annuleer</b> en dan op <b>Registreer je afwezigheid</b>.</section>".replace("%word", forbiddenWordFound),
              ),
            );
          } else {
            return constantField(null);
          }
        })
        .addField("only_visible_for_single_student", [], async () => {
          if (opts.userIsStudent === true) {
            return constantField(null);
          }
          if (lessongroup.students.length <= 1) {
            return constantField(null);
          }
          const options: {
            id: null | studentid;
            firstname: string;
            lastname: string;
          }[] = [
            { id: null, firstname: "Allemaal", lastname: "" },
            ...lessongroup.students
              .filter((s) => s.enrollment_status === "ActiveEnrollment")
              .orderBy(renderFirstNameLastName),
          ];

          return selectBox({
            values: options,
            initial: options[0],
            show: (s) => renderFirstNameLastName(s),
            label: "Aan student",
          });
        })
        .build();
      return (
        <div className={style.slide_in} style="margin: 8px">
          {form.render()}

          <div style="display: flex; justify-content: flex-end">
            {renderCancelButton({
              smallbuttons: opts.smallbuttons,
              onclick: () => writingS.set(null),
            })}
            {renderSaveButton({
              formS: form.s,
              save: (values) =>
                rpc(routes.saveTextNoteSpec, undefined, {
                  lessonid: lessonid,
                  content: values.content.trim(),
                  only_visible_for_single_student:
                    values.only_visible_for_single_student === null
                      ? values.only_visible_for_single_student
                      : values.only_visible_for_single_student.id,
                }).then(() => null),
              reload: opts.reload || true,
              timeout: 500,
              smallbuttons: opts.smallbuttons,
            })}
          </div>
        </div>
      );
    } else if (writing.tag === "writingfilenote") {
      const source: Source<{
        data: ArrayBuffer;
        mimetype: string;
        name: string;
      } | null> = new Source(null);
      return (
        <div className={style.slide_in} style="margin: 8px">
          <label>{"Nieuwe file"}</label>
          <div>
            {renderFileInput({
              source: source,
              initiallabel: "Kies een bestand",
              selectedlabel: "Bestand",
            })}
          </div>

          <div style="text-align: right">
            {renderCancelButton({
              smallbuttons: opts.smallbuttons,
              onclick: () => writingS.set(null),
            })}
            {dyn(source, function (file) {
              if (file === null) {
                return <span></span>;
              } else {
                const button = renderSaveButton({
                  save: () =>
                    rpc(routes.saveFileNoteSpec, undefined, {
                      lessonid,
                      mimetype: file.mimetype,
                      description: file.name,
                      fileb64: arrayBufferToBase64Client(file.data),
                    }).then(() => null),
                  reload: opts.reload || true,
                  smallbuttons: opts.smallbuttons,
                  timeout: 500,
                });
                return <span>{button}</span>;
              }
            })}
          </div>
        </div>
      );
    } else {
      return checkAllCasesHandled(writing);
    }
  });
}

export function renderExistingNote(
  schoolname: string,
  note: textnote | filenote,
  opts: { smallbuttons: boolean; reload?: () => void; isPastNote?: true },
  showOnlyVisibleForSingleStudent:
    | { tag: "no" }
    | {
        tag: "yes";
        students: { id: studentid; firstname: string; lastname: string }[];
      },
): HTMLElement | HTMLElement[] {
  const deletingS: Source<deletemode> = new Source("nothing");
  const author = note.student
    ? note.student.firstname + " " + note.student.lastname
    : note.teacher
    ? note.teacher.firstname + " " + note.teacher.lastname
    : schoolname;

  const toSpecificStudentRendered =
    showOnlyVisibleForSingleStudent.tag === "yes" &&
    "only_visible_for_single_student" in note &&
    note.only_visible_for_single_student !== null
      ? (() => {
          const stu = showOnlyVisibleForSingleStudent.students.find(
            (s) => s.id === note.only_visible_for_single_student,
          );
          return stu
            ? " " +
                "aan %student".replace(
                  "%student",
                  renderFirstNameLastName(stu),
                )
            : "";
        })()
      : "";

  const content = (function () {
    if ("storagefileid" in note) {
      return (
        <a
          href={routes.downloadFileNoteSpec.route.link({
            filenoteid: note.id,
            storagefileid: note.storagefileid,
          })}
          style="width: 100%;"
          target="_blank"
        >
          <div className={style.note_link_content}>{note.description}</div>
        </a>
      );
    } else {
      return Array.from(
        stringToJsxClientside(makeHtmlFromTextNote(note)).childNodes,
      ) as HTMLElement[];
    }
  })();

  const doDelete = () =>
    "storagefileid" in note
      ? rpc(routes.deleteFileNoteSpec, undefined, {
          filenoteid: note.id,
        })
      : rpc(routes.deleteTextNoteSpec, undefined, {
          textnoteid: note.id,
        });

  const customStyle = `
  font-size: 14px;
  padding: 8px 16px;
  flex: 1;
  ${opts.isPastNote ? "background-color: var(--grey-100);" : ""}
`;

  return (
    <div style="display: flex;">
      <div className={style.panel + style.note_contents} style={customStyle}>
        {content}
        {author ? (
          <div className={style.note_author}>
            - {author}
            {toSpecificStudentRendered}
            {opts.isPastNote ? (
              <span>
                {" - "}
                <b>{ddMMM(note.stamp.toLocalDate())}</b>
                <i
                  className={FA.icon_calendar_alt_regular}
                  style="margin-left: 8px; margin-right: 0px"
                ></i>
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ) : (
          <span></span>
        )}
        {renderDeleteOverlay(deletingS, doDelete, opts)}
      </div>
      {opts.isPastNote ? (
        <span></span>
      ) : (
        renderDeleteButton({ canDelete: note.candelete }, deletingS)
      )}
    </div>
  );
}

export function makeHtmlFromTextNote(note: { content: string }): string {
  const splitted = splitTextIntoUrls(note.content);
  return splitted
    .map((str) => {
      if (isUrl(str)) {
        // avoiding newlines
        const alink = `<a target="_blank" href="${str}">${str}</a>`;
        return alink;
      } else {
        return str;
      }
    })
    .join("")
    .split("\n\n")
    .map((paraText) => `<p>${paraText}</p>`)
    .join("")
    .split("\n")
    .join("<br/>");
}

function renderDeleteOverlay(
  deletingS: Source<deletemode>,
  doDelete: () => Promise<unknown>,
  opts: { smallbuttons: boolean; reload?: () => void },
): HTMLElement | HTMLElement[] {
  return dyn(deletingS, function (deleting) {
    if (deleting === "nothing") {
      return <span></span>;
    } else if (deleting === "confirming") {
      return (
        <div className={style.notes_delete_overlay}>
          {"Wil je deze notitie verwijderen?"}
          <div>
            <button
              className={style.button_small}
              onclick={() => deletingS.set("nothing")}
            >
              <i className={FA.icon_undo_solid}></i>
              {"Nee"}
            </button>
            <button
              className={style.button_small}
              onclick={() =>
                doDelete().then(() => {
                  deletingS.set("deleted");
                  setTimeout(
                    () =>
                      opts.reload ? opts.reload() : document.location.reload(),
                    2000,
                  );
                })
              }
            >
              <i className={FA.icon_trash_alt_solid}></i>
              {"Ja"}
            </button>
          </div>
        </div>
      );
    } else if (deleting === "deleted") {
      return (
        <div
          className={style.notes_delete_overlay}
          style="font-style: italic; background-color: #d4d4d4"
        >
          {"Verwijderd"}
        </div>
      );
    } else {
      return checkAllCasesHandled(deleting);
    }
  });
}

function renderDeleteButton(
  opts: { canDelete: boolean },
  deletingS: Source<deletemode>,
): HTMLElement {
  return opts.canDelete ? (
    dynStyle(
      deletingS,
      (deleting) => (deleting === "nothing" ? "" : "display: none!important;"),
      <button
        className={
          style.notes_delete_button +
          style.button_em_down +
          style.button_has_only_icon
        }
        onclick={() => deletingS.set("confirming")}
      >
        <i className={FA.icon_trash_alt_solid}></i>
      </button>,
    )
  ) : (
    <span></span>
  );
}

const urlRegex =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
function splitTextIntoUrls(str: string): string[] {
  return str.split(urlRegex);
}
function isUrl(str: string) {
  return urlRegex.test(str);
}
